import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Plus, Trash2, Calendar, Clock, Upload, FileText, Image, Edit2, Eye, EyeOff, ExternalLink, GripVertical, Sparkles, Ban } from 'lucide-react';

const EVENT_TYPES = [
  { id: 'menu_special', name: 'Menu spécial' },
  { id: 'soiree_thematique', name: 'Soirée thématique' },
  { id: 'privatisation', name: 'Privatisation' },
  { id: 'autre', name: 'Autre' }
];

const AdminEvents = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming'); // upcoming | past
  
  const [form, setForm] = useState({
    title: '',
    event_date: '',
    event_time: '',
    description: '',
    price_per_person: '',
    event_type: 'menu_special',
    display_order: 0,
    is_published: false,
    show_in_popup: false,
    is_sold_out: false,
    nouveau_badge_days: 7
  });

  const getAuthHeader = () => {
    const token = sessionStorage.getItem('adminAuth');
    if (!token) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${token}` };
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events`, { headers });
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const url = editingEvent 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${editingEvent.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/events`;
      
      const response = await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          display_order: parseInt(form.display_order) || 0,
          nouveau_badge_days: parseInt(form.nouveau_badge_days) || 7
        })
      });

      if (response.ok) {
        toast({ title: "Succès", description: editingEvent ? "Événement mis à jour" : "Événement créé" });
        resetForm();
        fetchEvents();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de sauvegarder", variant: "destructive" });
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) return;
    
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Événement supprimé" });
        fetchEvents();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de supprimer", variant: "destructive" });
    }
  };

  const handleImageUpload = async (eventId, file) => {
    const headers = getAuthHeader();
    if (!headers) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}/image`, {
        method: 'POST',
        headers: { 'Authorization': headers['Authorization'] },
        body: formData
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Image téléchargée" });
        fetchEvents();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Échec du téléchargement", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handlePdfUpload = async (eventId, file) => {
    const headers = getAuthHeader();
    if (!headers) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}/pdf`, {
        method: 'POST',
        headers: { 'Authorization': headers['Authorization'] },
        body: formData
      });

      if (response.ok) {
        toast({ title: "Succès", description: "PDF téléchargé" });
        fetchEvents();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Échec du téléchargement", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setForm({
      title: event.title,
      event_date: event.event_date,
      event_time: event.event_time,
      description: event.description || '',
      price_per_person: event.price_per_person || '',
      event_type: event.event_type || 'menu_special',
      display_order: event.display_order || 0,
      is_published: event.is_published,
      show_in_popup: event.show_in_popup,
      is_sold_out: event.is_sold_out || false,
      nouveau_badge_days: event.nouveau_badge_days || 7
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({
      title: '',
      event_date: '',
      event_time: '',
      description: '',
      price_per_person: '',
      event_type: 'menu_special',
      display_order: 0,
      is_published: false,
      show_in_popup: false,
      is_sold_out: false,
      nouveau_badge_days: 7
    });
    setEditingEvent(null);
    setShowForm(false);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const isPastEvent = (dateStr) => {
    const eventDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  };

  const getEventTypeName = (typeId) => {
    const type = EVENT_TYPES.find(t => t.id === typeId);
    return type ? type.name : typeId;
  };

  const upcomingEvents = events.filter(e => !isPastEvent(e.event_date));
  const pastEvents = events.filter(e => isPastEvent(e.event_date));
  const displayedEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-wide uppercase" style={{ fontFamily: 'Playfair Display, serif' }}>
                Événements
              </h1>
              <p className="text-sm text-gray-500">Gérer les événements du restaurant</p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-gray-900 hover:bg-gray-800">
            <Plus size={18} className="mr-2" />
            Nouvel événement
          </Button>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {editingEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Titre *</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Ex: Soirée Saint-Valentin"
                    required
                  />
                </div>
                <div>
                  <Label>Type d'événement</Label>
                  <select
                    value={form.event_type}
                    onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                  >
                    {EVENT_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Heure *</Label>
                  <Input
                    type="time"
                    value={form.event_time}
                    onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Prix par personne</Label>
                  <Input
                    value={form.price_per_person}
                    onChange={(e) => setForm({ ...form, price_per_person: e.target.value })}
                    placeholder="Ex: 85€"
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Description de l'événement (2-3 lignes)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Ordre d'affichage</Label>
                  <Input
                    type="number"
                    value={form.display_order}
                    onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                    placeholder="0 = priorité haute"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Plus le nombre est bas, plus l'événement apparaît en haut</p>
                </div>
                <div>
                  <Label>Badge "Nouveau" (jours)</Label>
                  <Input
                    type="number"
                    value={form.nouveau_badge_days}
                    onChange={(e) => setForm({ ...form, nouveau_badge_days: e.target.value })}
                    placeholder="7"
                    min="0"
                    max="30"
                  />
                  <p className="text-xs text-gray-500 mt-1">Nombre de jours pendant lesquels le badge "Nouveau" est affiché</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.is_published}
                    onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                  />
                  <Label>Publié sur le site</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.show_in_popup}
                    onCheckedChange={(v) => setForm({ ...form, show_in_popup: v })}
                  />
                  <Label>Afficher en pop-up</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.is_sold_out}
                    onCheckedChange={(v) => setForm({ ...form, is_sold_out: v })}
                  />
                  <Label>Complet</Label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-gray-900 hover:bg-gray-800">
                  {editingEvent ? 'Mettre à jour' : 'Créer l\'événement'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'upcoming' 
                ? 'bg-gray-900 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            À venir ({upcomingEvents.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'past' 
                ? 'bg-gray-900 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Passés ({pastEvents.length})
          </button>
        </div>

        {/* Events List */}
        <div className="space-y-4">
          {displayedEvents.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {activeTab === 'upcoming' ? 'Aucun événement à venir' : 'Aucun événement passé'}
              </h3>
              <p className="text-gray-500 mb-4">
                {activeTab === 'upcoming' 
                  ? 'Créez votre premier événement pour le voir apparaître ici.'
                  : 'Les événements passés apparaîtront ici.'}
              </p>
              {activeTab === 'upcoming' && (
                <Button onClick={() => setShowForm(true)} className="bg-gray-900 hover:bg-gray-800">
                  <Plus size={18} className="mr-2" />
                  Créer un événement
                </Button>
              )}
            </div>
          ) : (
            displayedEvents.map((event) => (
              <div
                key={event.id}
                className={`bg-white rounded-lg shadow p-6 ${activeTab === 'past' ? 'opacity-70' : ''}`}
              >
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Image */}
                  <div className="w-full md:w-48 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 relative">
                    {event.cover_image_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${event.cover_image_url}`}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Image size={32} />
                      </div>
                    )}
                    {event.display_order > 0 && (
                      <div className="absolute top-2 left-2 bg-gray-900 text-white text-xs px-2 py-1 rounded">
                        #{event.display_order}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-xl font-bold text-gray-900">{event.title}</h3>
                          {event.is_sold_out && (
                            <span className="flex items-center gap-1 text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">
                              <Ban size={10} /> Complet
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {formatDate(event.event_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {event.event_time}
                          </span>
                          <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                            {getEventTypeName(event.event_type)}
                          </span>
                          {event.price_per_person && (
                            <span className="font-semibold text-gray-900">{event.price_per_person}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {event.is_published ? (
                          <span className="flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            <Eye size={12} /> Publié
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            <EyeOff size={12} /> Brouillon
                          </span>
                        )}
                        {event.show_in_popup && (
                          <span className="flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                            <Sparkles size={12} /> Pop-up
                          </span>
                        )}
                      </div>
                    </div>

                    {event.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-3">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(event)}>
                        <Edit2 size={14} className="mr-1" /> Modifier
                      </Button>
                      
                      <a
                        href="/evenements"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <ExternalLink size={14} /> Prévisualiser
                      </a>
                      
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && handleImageUpload(event.id, e.target.files[0])}
                          className="hidden"
                        />
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <Image size={14} /> {event.cover_image_url ? 'Changer' : 'Image'}
                        </span>
                      </label>

                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => e.target.files[0] && handlePdfUpload(event.id, e.target.files[0])}
                          className="hidden"
                        />
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <FileText size={14} /> {event.pdf_url ? 'Changer PDF' : 'PDF'}
                        </span>
                      </label>

                      {event.pdf_url && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}${event.pdf_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Voir PDF
                        </a>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 ml-auto"
                        onClick={() => handleDelete(event.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminEvents;
